import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { unLinkTemplateParts } from "@/pages/templates/repositories/TemplatesRepositorie";

export const useTemplatePartsActionsAndButtons = ({
	gridRef,
	searchTemplateParts,
	setRemovePartError,
	TemplateId,
}: {
	TemplateId: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchTemplateParts: Function;
	setRemovePartError: (err: string) => void | null;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: { rowSelectedInPart, templatePartsList },
		actions: {
			setFirstTime,
			setRowSelectedInPart,
			setPartSearch,
			setDataPartSelected,
			setTemplatePartsList,
			setListPartsSelected,
		},
	} = useTemplatesContext();

	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = templatePartsList.filter(
				(item: any) => item.id !== -1,
			);

			setTemplatePartsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setPartSearch("");
			setDataPartSelected(undefined);
			searchTemplateParts();
		}
	}, [searchParams, TemplateId]);

	const cancelAddPart = useCallback(() => {
		const updatedList = templatePartsList.filter((item: any) => item.id !== -1);

		setTemplatePartsList(updatedList);
		setDataPartSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setPartSearch("");
	}, [
		templatePartsList,
		setTemplatePartsList,
		setDataPartSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
		setPartSearch,
	]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, TemplateId: TemplateId };
		const currentData = templatePartsList ? templatePartsList : [];
		const data = [newData, ...currentData];
		setTemplatePartsList(data);
	}, [templatePartsList, TemplateId]);

	const removeParts = useCallback(
		async (partsToRemove?: any[]) => {
			let formattedParts: { PartName: string; IsAssetResource: boolean }[] = [];

			if (partsToRemove && partsToRemove.length > 0) {
				formattedParts = partsToRemove.map((part: any) => ({
					PartName: part.data ? part.data.PartName : part.PartName,
					IsAssetResource: part.data
						? part.data.IsAssetResource
						: part.IsAssetResource,
				}));
			} else if (rowSelectedInPart) {
				formattedParts = [
					{
						PartName: rowSelectedInPart.PartName,
						IsAssetResource: rowSelectedInPart.IsAssetResource,
					},
				];
			}

			if (formattedParts.length > 0) {
				const res = await unLinkTemplateParts(
					TemplateId as number,
					formattedParts,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemovePartError(res.message);
				}
			}

			setRowSelectedInPart(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setPartSearch("");
			setListPartsSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			TemplateId,
			unLinkTemplateParts,
			refresh,
			setRemovePartError,
			rowSelectedInPart,
			setRowSelectedInPart,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setPartSearch,
			setListPartsSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		cancelAddPart,
		removeParts,
		addRow,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
